import React, { useRef, useState } from "react";
import { Button, Group } from "@mantine/core";
import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import {
  useCompleteJobTask,
  // useCreateReviewTasks,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { useNavigate, useParams } from "react-router-dom";
// import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "@tanstack/react-query";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  reset,
  setActiveModal,
  setPlaylistIndex,
  clearTaskPlaylist,
  setIsPlayMode,
} from "../store";
import { ViewMode } from "@/types";
import { useProfileHome } from "@/hooks/useProfileHome";
import { useConfig } from "statsig-react";
import { ConfidenceModal } from "./modals/ConfidenceModal";
import {
  hasQuestionOneAnswer,
  isValidEmptyArea,
  getNoMatchNeeded,
} from "../utils/helpers";
import { useTimeTracking } from "@/contexts/TimeTrackingContext";

interface StickyActionsBarProps {
  task: JobTaskDetailSchema;
  mode?: ViewMode;
  nextTaskId?: string;
  isPrefetchReady?: boolean;
}

export const StickyActionsBar: React.FC<StickyActionsBarProps> = ({
  task,
  mode,
  nextTaskId,
  isPrefetchReady,
}) => {
  const dispatch = useAppDispatch();
  const { getTimingData } = useTimeTracking();
  const { config: confidenceModalConfig } = useConfig(
    "confidence_modal_config",
  );
  // Sampled lazily, once per task, at the moment we need the answer. Drawing
  // during render is unstable (useMemo is a hint, not a guarantee, and React
  // may recompute), and drawing in an effect that writes a ref leaves the
  // value a render stale because ref writes don't re-render — either way the
  // sampling rate skews. Deferring to submit also means the Statsig config has
  // initialized by the time we read `percentage`, so we never draw against the
  // 0 default.
  const confidenceSampleRef = useRef<{
    taskId: string | null | undefined;
    show: boolean;
  } | null>(null);

  const shouldShowConfidenceModal = () => {
    const sample = confidenceSampleRef.current;
    if (!sample || sample.taskId !== task.id) {
      const next = {
        taskId: task.id,
        show: Math.random() < confidenceModalConfig.get("percentage", 0),
      };
      confidenceSampleRef.current = next;
      return next.show;
    }
    return sample.show;
  };

  // Loading state for buttons
  const [buttonsEnabled, setButtonsEnabled] = useState(true);
  const [noBtnLoading, setNoBtnLoading] = useState(false);
  const [yesBtnLoading, setYesBtnLoading] = useState(false);
  const emptyAreaConfirmed = useAppSelector(
    (state) => state.oos.emptyAreaConfirmed,
  );
  const itemDetectionConfirmed = useAppSelector(
    (state) => state.oos.itemDetectionConfirmed,
  );
  const planogramModalUsed = useAppSelector(
    (state) => state.oos.planogramModalUsed,
  );
  const productMatchConfirmed = useAppSelector(
    (state) => state.oos.productMatchConfirmed,
  );
  const resultMetadata = useAppSelector((state) => state.oos.resultMetadata);
  const selectedProductId = useAppSelector(
    (state) => state.oos.selectedProductId,
  );
  const selectedProductIds = useAppSelector(
    (state) => state.oos.selectedProductIds,
  );
  const planogramOverrideId = useAppSelector(
    (state) => state.oos.planogramOverrideId,
  );
  const groupedEventsIndex = useAppSelector(
    (state) => state.oos.groupedEventsIndex,
  );
  const questionOneHasAnswer = hasQuestionOneAnswer(
    emptyAreaConfirmed,
    resultMetadata,
  );
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { jobsPath } = useProfileHome();
  const queryClient = useQueryClient();
  const taskPlaylist = useAppSelector((state) => state.oos.taskPlaylist);
  const playlistIndex = useAppSelector((state) => state.oos.playlistIndex);
  const playlistActive = taskPlaylist.length > 0;

  // if the task is planogram only, logic will be different
  const planogramOnlyView =
    !!task?.data?.identified_product_id ||
    !!(task?.data as any)?.box_draw_task_id;

  // complete job task mutation
  const { mutateAsync: completeJobTask } = useCompleteJobTask(
    jobId || "",
    task.id || "",
  );

  const noMatchNeeded = getNoMatchNeeded(
    emptyAreaConfirmed,
    itemDetectionConfirmed,
    resultMetadata,
  );

  // create job task mutation
  // const { mutateAsync: createJobTask, isError: createJobTaskError } =
  //   useCreateReviewTasks();

  // useEffect(() => {
  //   if (createJobTaskError) {
  //     queryClient.setQueryData(["nextPendingTask", jobId], null);
  //     setButtonsEnabled(true);
  //   }
  // }, [createJobTaskError, queryClient, jobId]);

  // Reset Redux. If the prefetch is FULLY ready (task + parcel + galleries +
  // JPEG bytes all warm in cache), flip ["nextPendingTask", jobId] to the
  // prefetched task — the next task renders without a loading state.
  // Otherwise skip the flip and let invalidate handle it; flipping with
  // partial cache would just swap to the new task and immediately show a
  // LoadingOverlay while images fetch.
  //
  // Either way we invalidate so the backend's next-pending-task is the
  // source of truth — if the prefetch guessed wrong, the cache silently
  // updates in the background.
  const advanceToNextTask = () => {
    if (!jobId) return;
    dispatch(reset());

    // "My Tasks" play mode: walk the user's own assigned tasks by id rather than
    // pulling the job's next pending task. Advance the playlist and navigate to
    // the next assigned task; exit to the dashboard after the last one.
    if (playlistActive) {
      const nextIndex = playlistIndex + 1;
      if (nextIndex < taskPlaylist.length) {
        const next = taskPlaylist[nextIndex];
        dispatch(setPlaylistIndex(nextIndex));
        navigate(`/jobs/${next.jobId}/${next.route}?taskId=${next.taskId}`);
      } else {
        dispatch(clearTaskPlaylist());
        dispatch(setIsPlayMode(false));
        navigate(jobsPath);
      }
      return;
    }

    let flipped = false;
    if (isPrefetchReady && nextTaskId) {
      const prefetched = queryClient.getQueryData<JobTaskDetailSchema>([
        "fetch-task",
        jobId,
        nextTaskId,
      ]);
      if (prefetched) {
        queryClient.setQueryData(["nextPendingTask", jobId], prefetched);
        flipped = true;
      }
    }

    // Keep the prefetch's task list fresh so the next "first pending" target
    // reflects this completion instead of resolving to an already-seen task.
    queryClient.invalidateQueries({ queryKey: ["job-tasks", jobId] });

    if (flipped) {
      // Reconcile the optimistic flip against the authoritative endpoint.
      queryClient.invalidateQueries({ queryKey: ["nextPendingTask", jobId] });
    } else {
      // No confident prefetch to show. Drop the cached current task so the
      // view renders a loading state during reconciliation rather than
      // briefly re-rendering the task we just submitted. The mounted
      // useNextPendingTask observer refetches automatically once removed.
      queryClient.removeQueries({ queryKey: ["nextPendingTask", jobId] });
    }
  };

  // Builds the metadata object for result submission, optionally including confidence data
  const buildMetadata = (confidenceLevel?: string) => {
    const timingData = getTimingData();
    return {
      ...resultMetadata,
      grouped_oos_events_index: groupedEventsIndex,
      planogram_modal_used: planogramModalUsed,
      ...(confidenceLevel && { confidence_level: confidenceLevel }),
      ...(resultMetadata.confidence_modal_returned_to_edit && {
        confidence_modal_returned_to_edit: true,
      }),
      ...(timingData !== null && timingData),
    };
  };

  const handleYesClick = async ({
    confidenceLevel,
  }: { confidenceLevel?: string } = {}) => {
    setYesBtnLoading(true);
    setButtonsEnabled(false);

    try {
      await completeJobTask({
        result: {
          is_correct: true,
          empty_area_confirmed: emptyAreaConfirmed,
          empty_area_selected: true,
          item_detection_confirmed: itemDetectionConfirmed,
          product_match_confirmed: productMatchConfirmed,
          confirmation_product_id: selectedProductId,
          confirmation_product_ids:
            selectedProductIds.length > 0 ? selectedProductIds : undefined,
          override_planogram_id: planogramOverrideId || undefined,
          empty_areas_source: (task.data as any)?.empty_areas_source,
          ...(planogramOnlyView && {
            empty_area_to_aisle: (task.data as any)?.empty_area_to_aisle,
          }),
          metadata: buildMetadata(confidenceLevel),
        },
      });
      advanceToNextTask();
    } catch (error) {
      console.error(error);
    } finally {
      setYesBtnLoading(false);
      setButtonsEnabled(true);
    }
  };

  const handleNoClick = async ({
    forceNoProduct = false,
    confidenceLevel,
  }: { forceNoProduct?: boolean; confidenceLevel?: string } = {}) => {
    setNoBtnLoading(true);
    setButtonsEnabled(false);
    const emptyAreaResult = isValidEmptyArea(
      emptyAreaConfirmed,
      resultMetadata,
    );

    // in planogram only view or when planogram is not supported, we set the product match confirmed to false
    const noProduct = forceNoProduct || planogramOnlyView;
    const isProductMatchConfirmed = noProduct ? false : productMatchConfirmed;
    const confirmationProductId = noProduct ? null : selectedProductId;

    try {
      await completeJobTask({
        result: {
          is_correct: false,
          empty_area_confirmed: emptyAreaResult, // confirmation if the answer is acceptable for SpatialView
          empty_area_selected: emptyAreaConfirmed, // what the user selected for "Is Empty Area"
          item_detection_confirmed: itemDetectionConfirmed,
          product_match_confirmed: isProductMatchConfirmed,
          confirmation_product_id: confirmationProductId,
          confirmation_product_ids: planogramOnlyView
            ? undefined
            : selectedProductIds.length > 0
              ? selectedProductIds
              : undefined,
          override_planogram_id: planogramOverrideId || undefined,
          empty_areas_source: (task.data as any)?.empty_areas_source,
          ...(planogramOnlyView && {
            empty_area_to_aisle: (task.data as any)?.empty_area_to_aisle,
          }),
          metadata: buildMetadata(confidenceLevel),
        },
      });
      // NOTE: we used to create a OOS Selection task when a user clicks no
      // await createJobTask({
      //   store_id: task?.data?.internal_store_id as string,
      //   task_type: "OUT_OF_STOCK_SELECTION" as any,
      //   tasks: [
      //     {
      //       ...task,
      //       id: uuidv4(),
      //       type: "OUT_OF_STOCK_SELECTION" as any,
      //     },
      //   ],
      // });
      advanceToNextTask();
    } catch (error) {
      console.error(error);
    } finally {
      setNoBtnLoading(false);
      setButtonsEnabled(true);
    }
  };

  // Ref to store the pending submit action so the confidence modal can trigger it
  const pendingSubmitRef = useRef<((confidenceLevel: string) => void) | null>(
    null,
  );

  const executeSubmit = (confidenceLevel?: string) => {
    if (resultMetadata.blue_tag_present) {
      handleNoClick({ forceNoProduct: true, confidenceLevel });
      return;
    }
    const validEmptyArea = isValidEmptyArea(emptyAreaConfirmed, resultMetadata);
    const validItemDetection =
      (planogramOnlyView && itemDetectionConfirmed === null) ||
      itemDetectionConfirmed;
    if (validEmptyArea && validItemDetection && productMatchConfirmed) {
      handleYesClick({ confidenceLevel });
    } else {
      handleNoClick({ confidenceLevel });
    }
  };

  const handleSubmit = () => {
    if (shouldShowConfidenceModal() && mode === "viewer") {
      pendingSubmitRef.current = (confidenceLevel: string) => {
        executeSubmit(confidenceLevel);
      };
      dispatch(setActiveModal("confidence"));
    } else {
      executeSubmit();
    }
  };

  const handleConfidenceSubmit = (confidenceLevel: string) => {
    if (pendingSubmitRef.current) {
      pendingSubmitRef.current(confidenceLevel);
      pendingSubmitRef.current = null;
    }
  };

  const handleProductNotFound = () => {
    handleNoClick();
  };

  // Don't render for admin mode
  if (
    mode === "admin" ||
    mode === "confirmation-qa" ||
    mode === "confirmation-qa-feedback"
  ) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="flex justify-center p-4">
          <div className="w-full max-w-8xl">
            <Group justify="flex-end" gap="xl">
              {planogramOnlyView && (
                <Button
                  size="md"
                  onClick={handleProductNotFound}
                  className="min-w-[120px]"
                  variant="outline"
                  disabled={
                    !questionOneHasAnswer || noBtnLoading || yesBtnLoading
                  }
                >
                  Product Not Found
                </Button>
              )}
              <Button
                size="md"
                onClick={handleSubmit}
                className="min-w-[120px]"
                disabled={
                  !noMatchNeeded &&
                  (!buttonsEnabled ||
                    !questionOneHasAnswer ||
                    (itemDetectionConfirmed === null && !planogramOnlyView) ||
                    productMatchConfirmed === null)
                }
                loading={noBtnLoading || yesBtnLoading}
              >
                Submit
              </Button>
            </Group>
          </div>
        </div>
      </div>
      {mode === "viewer" && (
        <ConfidenceModal onSubmit={handleConfidenceSubmit} />
      )}
    </>
  );
};
